import React, { useEffect } from "react";
import { Globe, Star, ArrowsLeftRight, Bell, Target, MagnifyingGlass, Notebook, Sparkle, Stack, GearSix, X } from "@phosphor-icons/react";
import { useUiStore } from "../../store/uiStore";
import { useFeatures, FeatureUnavailable } from "../../context/FeatureContext";
import { MarketsPanel, WatchlistPanel, AlertsPanel, RioPanel, ScannerPanel, JournalPanel, ObjectsPanel, SettingsPanel } from "./panels";
import TradePanel from "./TradePanel";
import AiPanel from "./AiPanel";
import { useIsMobile } from "../../hooks/useIsMobile";

export const DOCK_ITEMS = [
  { id: "markets", label: "Markets", icon: Globe, feature: "platform.markets", Panel: MarketsPanel },
  { id: "watchlist", label: "Watchlist", icon: Star, feature: "platform.watchlist", Panel: WatchlistPanel },
  { id: "trade", label: "Trade", icon: ArrowsLeftRight, feature: "trading.ticket", Panel: TradePanel, accent: true, width: 380 },
  { id: "alerts", label: "Alerts", icon: Bell, feature: "alerts.in_app", Panel: AlertsPanel },
  { id: "rio", label: "R.I.O", icon: Target, feature: "analysis.rio_score", Panel: RioPanel },
  { id: "scanner", label: "Scanner", icon: MagnifyingGlass, feature: "analysis.scanner", Panel: ScannerPanel },
  { id: "journal", label: "Journal", icon: Notebook, feature: "platform.journal", Panel: JournalPanel, width: 420 },
  { id: "ai", label: "AI", icon: Sparkle, feature: "ai.claude", Panel: AiPanel, width: 400 },
  { id: "objects", label: "Objects", icon: Stack, feature: "chart.drawings", Panel: ObjectsPanel, group: 2 },
  { id: "settings", label: "Settings", icon: GearSix, feature: null, Panel: SettingsPanel, group: 2 },
];

// Right utility dock: collapsed icon rail + ONE sliding panel over the chart workspace (ESC closes).
export default function UtilityDock() {
  const { dock, setPanel, closePanel } = useUiStore();
  const { enabled, status } = useFeatures();
  const mobile = useIsMobile();
  const active = DOCK_ITEMS.find((d) => d.id === dock.activePanel) || null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && dock.activePanel && !e.target.closest?.("input, textarea")) closePanel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dock.activePanel, closePanel]);

  const visible = DOCK_ITEMS.filter((d) => !d.feature || status(d.feature) !== "off");
  const Panel = active?.Panel;
  const body = active && (active.feature && !enabled(active.feature) ? <FeatureUnavailable name={active.label} /> : <Panel />);

  const railButton = (d, i) => {
    const Icon = d.icon;
    const on = dock.activePanel === d.id;
    const gated = d.feature && !enabled(d.feature);
    return (
      <React.Fragment key={d.id}>
        {d.group === 2 && visible[i - 1]?.group !== 2 && <div className={mobile ? "mx-1 w-px h-8 bg-border shrink-0" : "my-1.5 h-px w-8 bg-border"} />}
        <button data-testid={`dock-${d.id}`} onClick={() => setPanel(d.id)} title={gated ? `${d.label} · unavailable` : d.label} aria-pressed={on} aria-label={d.label}
          className={`relative ${mobile ? "min-w-[64px] shrink-0 py-1.5" : "w-[60px] py-2"} rounded-lg flex flex-col items-center gap-1 text-[10px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${on ? "bg-primary/15 text-primary" : gated ? "text-muted-foreground/40 hover:text-muted-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"} ${d.accent && !on ? "text-primary/80" : ""}`}>
          {on && !mobile && <span className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-l bg-primary" />}
          {on && mobile && <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-6 rounded-b bg-primary" />}
          <Icon size={20} weight={on ? "fill" : "duotone"} />
          <span>{d.label}</span>
        </button>
      </React.Fragment>
    );
  };

  if (mobile) {
    // Mobile: bottom tab bar + the active utility as a bottom sheet over the chart (chart stays visible above it).
    return (
      <div className="w-full shrink-0" data-testid="utility-dock">
        {active && (
          <>
            <div className="fixed inset-0 z-30 bg-black/40" data-testid="dock-backdrop" onClick={closePanel} />
            <aside data-testid={`dock-panel-${active.id}`} role="complementary" aria-label={`${active.label} panel`}
              className="fixed inset-x-0 bottom-14 z-40 h-[68dvh] rounded-t-2xl border-t border-border bg-panel/95 backdrop-blur-md flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-200">
              <div className="h-10 px-3 border-b border-border flex items-center gap-2 shrink-0">
                <span className="absolute left-1/2 -translate-x-1/2 top-1.5 h-1 w-10 rounded-full bg-border" />
                <active.icon size={16} weight="duotone" className="text-primary" />
                <span className="text-sm font-semibold" data-testid="dock-panel-title">{active.label}</span>
                <button data-testid="dock-panel-close" onClick={closePanel} title="Close" className="ml-auto h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"><X size={14} /></button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none">{body}</div>
            </aside>
          </>
        )}
        <nav className="h-14 w-full border-t border-border bg-panel flex items-center overflow-x-auto scrollbar-none px-1 gap-0.5 relative z-40" data-testid="dock-rail" aria-label="Utility dock">
          {visible.map(railButton)}
        </nav>
      </div>
    );
  }

  return (
    <div className="flex h-full shrink-0" data-testid="utility-dock">
      {active && (
        <aside data-testid={`dock-panel-${active.id}`} className="h-full border-l border-border bg-panel/95 backdrop-blur-md flex flex-col animate-in slide-in-from-right-4 fade-in duration-200"
          style={{ width: Math.min(active.width || dock.width, Math.max(300, window.innerWidth * 0.3)) }} role="complementary" aria-label={`${active.label} panel`}>
          <div className="h-10 px-3 border-b border-border flex items-center gap-2 shrink-0">
            <active.icon size={16} weight="duotone" className="text-primary" />
            <span className="text-sm font-semibold" data-testid="dock-panel-title">{active.label}</span>
            <button data-testid="dock-panel-close" onClick={closePanel} title="Close (Esc)" className="ml-auto h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground"><X size={14} /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none">{body}</div>
        </aside>
      )}
      <nav className="w-[72px] h-full border-l border-border bg-panel flex flex-col items-center py-2 gap-0.5 shrink-0" data-testid="dock-rail" aria-label="Utility dock">
        {visible.map(railButton)}
      </nav>
    </div>
  );
}
